import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { HasAccess } from "@/components/common/has-access";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { useCreateNote } from "../hooks/use-create-note";

interface UserNotesSectionProps {
  userId: string;
  initialNote?: string | null;
  lastUpdated?: string | null;
  lastUpdatedBy?: string | null;
}

export function UserNotesSection({
  userId,
  initialNote = "",
  lastUpdated,
  lastUpdatedBy,
}: UserNotesSectionProps) {
  const [note, setNote] = useState(initialNote || "");
  const { mutate: createNote, isPending: isSaving } = useCreateNote();
  const { toast } = useToast();

  // Update local state when initialNote changes (e.g., after refetch)
  useEffect(() => {
    setNote(initialNote || "");
  }, [initialNote]);

  const handleSave = async () => {
    if (!note.trim()) {
      toast({
        title: "Error",
        description: "Note cannot be empty",
        variant: "destructive",
      });
      return;
    }

    createNote({
      user_id: userId,
      content: note.trim(),
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return format(date, "dd MMM yyyy").toUpperCase();
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="notes" className="text-orange-500 font-bold text-base">
        Notes
      </Label>
      <div className="flex gap-3">
        <Textarea
          id="notes"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add notes about this user..."
          className="flex-1 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 min-h-[50px]"
        />
        <HasAccess permission="write">
          <Button
            onClick={handleSave}
            disabled={isSaving}
            variant="outline"
            className="bg-white text-orange-500 border-orange-500 hover:bg-orange-50 h-fit"
          >
            Save
          </Button>
        </HasAccess>
      </div>
      {lastUpdated && (
        <p className="text-xs text-gray-500">
          LAST UPDATED ON {formatDate(lastUpdated)}
          {lastUpdatedBy && ` BY ${lastUpdatedBy.toUpperCase()}`}
        </p>
      )}
    </div>
  );
}

